import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Instagram, Facebook, Youtube, ShoppingBag, UserCircle2 } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';

const Header = () => {
  const siteConfig = useSiteConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Accueil' },
    { path: '/quest-ce-que-la-gr', label: 'La GR' },
    { path: '/notre-club', label: 'Notre Club' },
    { path: '/inscriptions', label: 'Inscriptions' },
    { path: '/resultats', label: 'Nos Succès' },
    { path: '/actualites', label: 'Actualités' },
    { path: '/boutique', label: 'Boutique' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative z-50">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-3"
            >
              <img 
                src="/images/logo.png" 
                alt="GR La Saline Logo" 
                className="h-12 md:h-16 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-fuchsia-400 via-pink-400 to-fuchsia-500 bg-clip-text text-transparent">
                  {siteConfig.clubName}
                </span>
                <span className="text-xs text-white/60 tracking-widest uppercase hidden md:block">
                  {siteConfig.tagline}
                </span>
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="relative group"
              >
                <span className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === item.path
                    ? 'text-fuchsia-400'
                    : 'text-white hover:text-fuchsia-300'
                }`}>
                  {item.label}
                </span>
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-fuchsia-500 to-pink-500"
                  />
                )}
              </Link>
            ))}
            
            {/* Social Icons */}
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-white/20">
              <motion.a
                href={siteConfig.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="text-white/60 hover:text-fuchsia-400 transition-colors"
              >
                <Facebook size={18} />
              </motion.a>
              <motion.a
                href={siteConfig.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="text-white/60 hover:text-fuchsia-400 transition-colors"
              >
                <Instagram size={18} />
              </motion.a>
              <motion.a
                href={siteConfig.socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="text-white/60 hover:text-fuchsia-400 transition-colors"
              >
                <Youtube size={18} />
              </motion.a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative z-50 text-white p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="container mx-auto px-6 py-8 flex flex-col gap-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`text-lg font-medium ${
                      location.pathname === item.path
                        ? 'text-fuchsia-400'
                        : 'text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <a href={siteConfig.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-white/60">
                  <Facebook size={20} />
                </a>
                <a href={siteConfig.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-white/60">
                  <Instagram size={20} />
                </a>
                <a href={siteConfig.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-white/60">
                  <Youtube size={20} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
};

export default Header;
